import { useAuthState } from '@auth/hooks';
import { HeroBackground, SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Container, Section, ButtonType, Badge, IconButton, Edit, IconSize, BadgeSize, BadgeVariant } from '@soteria/design-system-ve';
import { LabelPolicyDialog, StandardLabelDialog } from '@system-config/label-management/components';
import { SortableDataList, type SortableTableColumn, type SortOption } from '@system-config/label-management/components/SortableDataList';
import type { StandardLabel } from '@system-config/label-management/domain';
import { useDeleteStandardLabel, useStandardLabels } from '@system-config/label-management/hooks';
import { useState } from 'react';

import * as styles from './LabelManagementPage.css';

function LabelManagementPageComponent() {
  // Check if user is system admin - only system admins can access label management
  const { isUserSystemAdmin } = useAuthState();

  const { labels, loading, errors } = useStandardLabels();
  const { deleteStandardLabel, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteStandardLabel();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPolicyDialogOpen, setIsPolicyDialogOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<StandardLabel | undefined>(undefined);

  // If user is not a system admin, don't render anything
  if (!isUserSystemAdmin) {
    return null;
  }

  const handleOpenDialog = (label?: StandardLabel) => {
    setSelectedLabel(label || undefined);
    setIsDialogOpen(true);
  };

  const handleOpenPolicyDialog = (label: StandardLabel) => {
    setSelectedLabel(label);
    setIsPolicyDialogOpen(true);
  };

  // Create the delete item context
  const deleteItemContext = {
    handler: async (label: StandardLabel) => await deleteStandardLabel(label.id),
    isDeleting,
    errors: deleteErrors,
    resetErrors,
  };

  const columns: SortableTableColumn<StandardLabel>[] = [
    {
      key: 'labelName',
      header: 'Label Name',
      render: item => item.labelName,
      width: '20%',
      sortable: true,
      sortKey: 'labelName',
    },
    {
      key: 'labelShortName',
      header: 'Short Name',
      render: item => item.labelShortName || '-',
      width: '15%',
      sortable: true,
      sortKey: 'labelShortName',
    },
    {
      key: 'contentType',
      header: 'Content Type',
      render: item => <Badge size={BadgeSize.Medium} text={item.contentType?.toUpperCase()} />,
      width: '12%',
      sortable: true,
      sortKey: 'contentType',
    },
    {
      key: 'policy',
      header: 'Policy',
      render: item => (
        <div className={styles.policyContainer}>
          <span>{item.policy}</span>
          <IconButton
            aria-label="Edit Policy Content"
            icon={Edit}
            iconSize={IconSize.XSmall}
            title="Edit Policy Content"
            onClick={e => {
              e.stopPropagation(); // Prevent row selection
              handleOpenPolicyDialog(item);
            }}
          />
        </div>
      ),
      width: '20%',
      sortable: true,
      sortKey: 'policy',
    },
    {
      key: 'labelStatus',
      header: 'Status',
      render: item => {
        const status = item.labelStatus.toLowerCase();
        const variant = status === 'approved' ? BadgeVariant.Approved : status === 'pending' ? BadgeVariant.Pending : BadgeVariant.Default;

        return <Badge text={item.labelStatus} variant={variant} />;
      },
      width: '15%',
      sortable: true,
      sortKey: 'labelStatus',
    },
    {
      key: 'labelNumMap',
      header: 'Numeric Mapping',
      render: item => item.labelNumMap,
      width: '20%',
      sortable: true,
      sortKey: 'labelNumMap',
    },
  ];

  // Sort options for the filter bar
  const sortOptions: SortOption<StandardLabel>[] = [
    { value: 'labelName', label: 'Label Name', sortKey: 'labelName' },
    { value: 'labelShortName', label: 'Short Name', sortKey: 'labelShortName' },
    { value: 'contentType', label: 'Content Type', sortKey: 'contentType' },
    { value: 'policy', label: 'Policy', sortKey: 'policy' },
    { value: 'labelStatus', label: 'Status', sortKey: 'labelStatus' },
    { value: 'labelNumMap', label: 'Numeric Mapping', sortKey: 'labelNumMap' },
  ];

  return (
    <>
      <HeroBackground />
      <Container className={styles.withHeroBackground} padding="xxlarge" size="xxl">
        <Section spacing="large">
          <SectionHeader
            action={{
              text: 'ADD LABEL',
              onClick: () => handleOpenDialog(undefined),
              buttonType: ButtonType.Primary,
            }}
            description="Manage standard labels across communities"
            title="Standard Labels"
          />

          {/* Standard Labels List */}
          <SortableDataList
            searchable
            showFilterBar
            columns={columns}
            data={labels}
            dataTypeName="standard label"
            defaultSort={{ key: 'labelName', direction: 'asc' }}
            deleteItemContext={deleteItemContext}
            error={errors}
            isLoading={loading}
            searchKey="labelName"
            searchPlaceholder="Search labels by name..."
            sortOptions={sortOptions}
            onEdit={handleOpenDialog}
          />
        </Section>

        {/* Create/Edit Standard Label Dialog */}
        <StandardLabelDialog isOpen={isDialogOpen} label={selectedLabel} onClose={() => setIsDialogOpen(false)} />

        {/* Policy Content Dialog */}
        <LabelPolicyDialog isOpen={isPolicyDialogOpen} label={selectedLabel} onClose={() => setIsPolicyDialogOpen(false)} />
      </Container>
    </>
  );
}

export const LabelManagementPage = withAppInsightsTracking(LabelManagementPageComponent, 'LabelManagementPage');
